import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Target, Award, AlertTriangle } from "lucide-react";
import type { AOFormData, Evaluation, CritereEvaluation } from "../AOWizard";
import { EvaluationsSummaryCard } from "./EvaluationsSummaryCard";

interface AOConfigStepProps {
  formData: AOFormData;
  setFormData: (data: AOFormData) => void;
  evaluations: Evaluation[];
  criteresMap: Record<number, CritereEvaluation[]>;
}

export function AOConfigStep({ formData, setFormData, evaluations, criteresMap }: AOConfigStepProps) {
  const handleInputChange = (field: keyof AOFormData, value: string | number) => {
    setFormData({ ...formData, [field]: value });
  };

  const getDecisionBadge = (score: number) => {
    if (score >= formData.seuil_shortlist) {
      return <Badge className="bg-success text-success-foreground">Shortlist</Badge>;
    } else if (score >= formData.seuil_consider) {
      return <Badge className="bg-warning text-warning-foreground">Consider</Badge>;
    } else {
      return <Badge className="bg-destructive text-destructive-foreground">Reject</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Seuils de décision */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Target className="h-5 w-5 mr-2" />
            Seuils de décision
          </CardTitle>
          <CardDescription>
            Définissez les seuils de score qui détermineront la catégorisation automatique des candidatures
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Seuil Shortlist</Label>
                <span className="text-lg font-medium text-success">{formData.seuil_shortlist} pts</span>
              </div>
              <Slider
                value={[formData.seuil_shortlist]}
                onValueChange={(value) => handleInputChange('seuil_shortlist', value[0])}
                min={formData.seuil_consider + 1}
                max={100}
                step={1}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Candidatures à retenir en priorité
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Seuil Consider</Label>
                <span className="text-lg font-medium text-warning">{formData.seuil_consider} pts</span>
              </div>
              <Slider
                value={[formData.seuil_consider]}
                onValueChange={(value) => handleInputChange('seuil_consider', value[0])}
                min={0}
                max={formData.seuil_shortlist - 1}
                step={1}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Candidatures à examiner selon le contexte
              </p>
            </div>
          </div>

          {/* Aperçu des décisions */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="text-sm font-medium mb-3">Aperçu des catégories</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm">≥ {formData.seuil_shortlist} points</span>
                {getDecisionBadge(formData.seuil_shortlist)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">{formData.seuil_consider} - {formData.seuil_shortlist - 1} points</span>
                {getDecisionBadge(formData.seuil_consider)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">&lt; {formData.seuil_consider} points</span>
                {getDecisionBadge(formData.seuil_consider - 1)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configuration du statut */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Award className="h-5 w-5 mr-2" />
            Statut de l'appel d'offres
          </CardTitle>
          <CardDescription>
            Définissez le statut initial de votre appel d'offres
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="statut">Statut</Label>
            <Select
              value={formData.statut}
              onValueChange={(value) => handleInputChange('statut', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en cours">En cours de rédaction</SelectItem>
                <SelectItem value="publié">Publié</SelectItem>
                <SelectItem value="terminé">Terminé</SelectItem>
              </SelectContent>
            </Select>
            
            {formData.statut === 'publié' && (
              <div className="flex items-start space-x-2 p-3 bg-warning/10 border border-warning/20 rounded-md mt-3">
                <AlertTriangle className="h-4 w-4 text-warning mt-0.5" />
                <div className="text-xs text-warning-foreground">
                  <p className="font-medium">Attention</p>
                  <p>En publiant l'appel d'offres, assurez-vous que tous les critères sont correctement définis et que les informations sont complètes.</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Résumé de configuration */}
      <Card className="card-shadow">
        <CardHeader>
          <CardTitle>Résumé de la configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <Label className="text-xs text-muted-foreground">TITRE</Label>
              <p className="font-medium">{formData.titre || "Non défini"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">RÉFÉRENCE</Label>
              <p className="font-medium">{formData.reference || "Non définie"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">DATE LIMITE</Label>
              <p className="font-medium">
                {formData.date_limite ? new Date(formData.date_limite).toLocaleDateString('fr-FR') : "Non définie"}
              </p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">STATUT</Label>
              <p className="font-medium">{formData.statut}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">SEUIL SHORTLIST</Label>
              <p className="font-medium text-success">{formData.seuil_shortlist} points</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">SEUIL CONSIDER</Label>
              <p className="font-medium text-warning">{formData.seuil_consider} points</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Résumé des évaluations et critères */}
      <EvaluationsSummaryCard 
        evaluations={evaluations}
        criteresMap={criteresMap}
      />
    </div>
  );
}